import { node } from 'prop-types'
import type { CSSProperties } from 'react'
import { Header } from 'semantic-ui-react'
import ImgTemplate from '../../../../utilites/ImgTemplate'

const paragraphStyle = {
  marginBottom: '1em',
  textAlign: 'justify',
} satisfies CSSProperties

function Heliophysics() {
  return (
    <div className='fade-in'>
      <Header as='h1' style={{ textAlign: 'center' }}>
        Heliophysics
      </Header>

      <div style={paragraphStyle}>
        Heliophysics (from the prefix "helio", derived from Attic Greek “hḗlios”, meaning "Sun", and the noun “physics”)
        is the study of the Sun and its influence on the solar system. It encompasses the investigation of the Sun as a
        star—its internal structure, the structure and dynamics of its atmosphere, magnetic activity, and energetic
        phenomena such as solar flares—as well as the solar wind and space weather, including the interactions between
        the solar-driven space environment and the planets and their magnetospheres. In essence, heliophysics examines
        the dynamic system formed by the Sun, the heliosphere, and the surrounding space environment.
      </div>

      <h3>SIZE AND TEMPERATURE OF THE SUN</h3>
      <div style={paragraphStyle}>
        The Sun, when viewed with the naked eye, usually appears as a perfectly smooth disk, shining with a blinding
        white-yellow light. Its apparent diameter changes due to the elliptical shape of the Earth's orbit, from 31.4
        arcminutes in July to 32.5 arcminutes in January. The Sun, like all main-sequence stars, is a gigantic plasma
        sphere, whose density decreases (in some layers even quite rapidly) with distance from the center, but nowhere
        does it abruptly drop to zero. Therefore, the surface of the Sun is conventionally defined as the lower part of
        the photosphere, a thin layer of plasma only about 500 km thick, which emits the vast majority of visible
        radiation. Defined this way, the Sun’s diameter is 1,392,520 km (which is over 109 Earth diameters), and its
        surface area is 6.1×10<sup>9</sup> km<sub>2</sub>, making it nearly 12,000 times larger than the surface area of
        the Earth.
      </div>

      <div style={paragraphStyle}>
        The emission of the Sun's photosphere in the visible part of the continuous spectrum is almost identical to the
        emission of a perfect black body (i.e., one that completely absorbs all incident radiation) at a temperature of
        5777 K. This temperature is called the Sun’s effective temperature, and it is usually considered the surface
        temperature of the Sun. However, the physical kinetic temperature of the photospheric plasma is locally much
        lower; in the so-called temperature minimum region it drops to about 4200 K, but individual volumes of plasma
        can locally have even lower temperatures.
      </div>

      <div style={paragraphStyle}>
        The layers of solar plasma lying above the photosphere, called the solar atmosphere, emit radiation at various
        wavelengths, from X-rays to radio waves. The typical plasma temperature in quiet regions of the solar corona
        ranges from 1 to 2 MK (million kelvins), but locally, in post-flare loops, it can reach 10 MK or more.
      </div>

      <div style={paragraphStyle}>
        Since the Sun is a plasma sphere, and from its upper atmospheric layer (the corona) plasma continuously flows
        out forming the solar wind, there is no physical upper boundary surface of the Sun, and thus the physical radius
        of the Sun as a plasma sphere is not defined.
      </div>

      <div style={paragraphStyle}>
        The solar thermonuclear reactor maintains equilibrium and does not explode under the influence of the gigantic
        plasma pressure, which in the core reaches 2.37×10<sup>16</sup> N/m<sup>2</sup>, and the enormous radiation
        pressure, only because these are precisely balanced by the equally immense gravitational force of the Sun’s
        mass. The mass of the Sun is 1.989×10<sup>30</sup> kg, which means that the Sun contains 99.9% of the total mass
        of the entire Solar System, or that its mass is 332,900 times greater than the mass of the Earth. Although the
        Sun is very massive, the center of mass of the entire Solar System does not coincide with the geometric center
        of the Sun. The center of the Sun also orbits the center of the Solar System, but along a very tight and complex
        trajectory.
      </div>

      <div style={paragraphStyle}>
        In addition to the constant loss of mass caused by the emission of energy at a rate of 4,270,000 tons per
        second, the Sun also loses mass due to the instability of its outermost layer, called the solar corona. This
        layer gradually flows out of the Sun into interplanetary space in the form of a plasma stream known as the solar
        wind. The mass loss caused by the solar wind amounts to about 1,000,000 tons per second. Therefore, every second
        the mass of the Sun decreases by a total of approximately 5,300,000 tons. However, since the Sun is so massive,
        it loses only less than 3×10<sup>-21</sup> of its total mass per second, which means that over the course of its
        entire existence so far, about 4.6 billion years, its mass has decreased by only 0.04%. This has no effect on
        its evolution.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/Solarsystem1.1.webp' alt='Solar System' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://janus.astro.umd.edu/front/pages/links/Sizes1.html'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            janus.astro.umd.edu
          </a>
        </p>
      </div>

      <h3>Energy Production Inside the Sun</h3>
      <div style={paragraphStyle}>
        The Sun is practically the only source of energy reaching the Earth from space. The power of the Sun’s radiation
        is 3.8×10<sup>26</sup> W, which means that in every second the Sun emits enough energy to boil icy water
        contained in a cube with a side of 971 km. On Earth, in all oceans, seas, rivers, ice, and in the atmosphere,
        there is a total of only 1.36×10<sup>9</sup> km<sup>3</sup> of water, meaning that all the Earth’s water would
        fit into a cube with a side of just 1113 km, and the energy emitted by the Sun would boil it in about one and a
        half seconds. The Sun’s emission in the visible part of the continuous spectrum is almost identical to the
        emission of a perfect black body at a temperature of 5777 K. This temperature is called the Sun’s effective
        temperature.
      </div>

      <div style={paragraphStyle}>
        In the core of the Sun, the plasma temperature reaches 15,700,000 K and the density 153,000 kg/m<sup>3</sup>
        (although it is plasma, i.e., gas, its local density is more than 13 times greater than the density of lead).
        Under such conditions of temperature and pressure, thermonuclear fusion reactions occur very efficiently, in
        which four hydrogen nuclei (protons) are transformed into a helium nucleus, whose mass is 0.7% less than the
        mass of the four protons. This mass difference corresponds to the energy released in the process of forming the
        helium nucleus. The formation of one helium nucleus does not generate a large amount of energy, because the mass
        of a proton is only 1.67×10<sup>-27</sup> kg, but in the Sun’s core, over 600 million tons of protons are
        transformed into helium every second, and thus 4,270,000 tons of matter are transformed into energy, determined
        by E = mc<sup>2</sup>. Since c is very large, 299,792,458 m/s, the transformation of such a large mass results
        in the generation of a tremendous amount of energy.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/Proton Proton.webp' alt='Proton' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://jtg.sjrdesign.net/advanced/sun_astar.html'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            jtg.sjrdesign.net
          </a>
        </p>
      </div>

      <h3>CHEMICAL COMPOSITION OF THE SUN</h3>
      <div style={paragraphStyle}>
        The Sun as a whole is composed mainly of hydrogen (71% of the Sun’s mass, 91% of the atoms in the Sun) and
        helium (27% of the Sun’s mass, 8.9% of the atoms in the Sun). All heavier elements, including O, C, N, Si, Mg
        and all others—even gold—make up only about 2% of its mass. Thermonuclear reactions occur only near the center
        of the Sun, in a spherical region called the core, which extends to about 0.25 of the solar radius from the
        center and includes only 1.6% of the Sun’s volume beneath the photosphere! However, due to the enormous density
        of matter, this tiny volume contains as much as 50% of the Sun’s mass. Since the plasma in the inner part of the
        Sun is stable and no convective motions occur within it, it does not mix with the matter located above.
        Therefore, energy generation in the core happens at the expense of the depletion of hydrogen from a limited
        supply of material. It is estimated that so far, about 45% of the hydrogen in the Sun’s core has already been
        converted into helium, meaning its reserve has been significantly reduced. In the relatively distant future, in
        about 5 billion years, this will lead to a dramatic fuel crisis in the Sun, which will eventually cause the Sun
        to transform first into a red giant (in about 7 billion years), and then into a white dwarf.
      </div>


      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/sun_elements.webp' alt='Sun elements' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://jtg.sjrdesign.net/advanced/sun_astar.html'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            jtg.sjrdesign.net
          </a>
        </p>
      </div>

      <h3>Energy Transport Inside the Sun</h3>
      <div style={paragraphStyle}>
        The energy generated in the core of the Sun gradually flows toward the surface. Up to a distance of 515,000 km
        from the center of the Sun (0.71 of the solar radius), the solar plasma is sufficiently transparent that energy
        transport occurs via photons—that is, through radiation.
      </div>
      <div style={paragraphStyle}>
        This method of energy transfer is called radiative transfer, and the corresponding region of the Sun is known as
        the radiative zone. In the radiative zone, photons are constantly absorbed, scattered, and re-emitted, and as
        they move toward the surface, their individual energy gradually decreases, adapting to the local plasma
        parameters. This journey of photons through the Sun's interior takes a very long time! Traveling in a straight
        line, a photon would cover the distance from the Sun's center to the photosphere in just 2.7 seconds; however,
        constant "zigzagging" toward the surface, caused by interactions with matter, means that the energy currently
        reaching the surface of the Sun was actually produced in its core about 200,000 years ago! The mass of plasma
        contained in the core and the radiative zone together accounts for as much as 98% of the total mass of our star.
      </div>

      <div style={paragraphStyle}>
        The solar plasma located above the radiative zone (above 0.71 of the solar radius) is much cooler and therefore
        significantly less transparent. As a result, radiation alone is no longer capable of carrying the incoming
        energy from the core. The plasma near the base of this layer becomes overheated and convection is triggered—that
        is, massive bubbles of hot plasma rise toward the surface through the slightly cooler surroundings. For this
        reason, the outer portion of the Sun’s interior is called the convective zone. Its thickness is about 181,000
        km. The upper surface of the convective zone is the photosphere, where the smallest convective cells are easily
        visible in the form of granulation. Despite encompassing a very thick layer, the convective motions involve,
        due to the low density, less than 2% of the Sun's mass.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/solar_anatomy.webp' alt='Solar Anatomy' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://science.nasa.gov/learn/heat/big-ideas/big-idea-3-2/'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            science.nasa.gov
          </a>
        </p>
      </div>

      <h3>The Edge of the Solar Disk</h3>
      <div style={paragraphStyle}>
        The plasma density in the region of the so-called temperature minimum is only 4.9 × 10<sup>-6</sup>{' '}
        kg/m<sup>3</sup>—that is, 33 billion times less than the plasma density in the Sun’s core, and even 272,000
        times less than the density of Earth’s atmosphere. Meanwhile, the geometric thickness of the photosphere is
        about 500 km, which is quite significant by Earth standards. So why does the edge of the Sun in visible light
        not appear blurred, but instead looks very sharp?
      </div>
      <div style={paragraphStyle}>
        Regardless of the direction in which we observe the photosphere—whether almost "straight down" (near the center
        of the solar disk) or nearly "tangentially" (near the edge of the disk)—even a short section along the line of
        sight contains enough matter to become effectively opaque. That means a photon emitted from deeper within the
        Sun has practically no chance of escaping without interacting with matter.
      </div>
      <div style={paragraphStyle}>
        Therefore, because the thickness of the photosphere is very small relative to the Sun’s radius (less than 0.1%;
        500 km vs. 692,000 km), and because we observe the Sun from a considerable distance of about 150 million
        kilometers, the edge of its disk becomes imperceptibly thin—thus it appears perfectly sharp to us.
      </div>

      <h3>Limb Darkening</h3>
      <div style={paragraphStyle}>
        The brightness of the photosphere is not uniform across the entire surface of the visible solar disk. Using a
        safe method of solar observation, such as the projection method, one can easily notice, upon careful inspection
        of the brightness in different parts of the solar disk image, that its brightness significantly decreases
        toward the edges. This phenomenon is called <strong>limb darkening.</strong>
      </div>
      
      <div style={paragraphStyle}>
        The photosphere is a layer of plasma in which both temperature and density increase very rapidly with depth: the
        plasma density at a depth of 500 km below the temperature minimum reaches about 2.7 × 10<sup>-4</sup>{' '}
        kg/m<sup>3</sup>, which is more than 50 times greater than just 500 kilometers above. The plasma temperature at
        that depth rises to about 5800 K, which is an increase of 1600 K.
      </div>
      
      <div style={paragraphStyle}>
        Near the center of the solar disk, the observer looks at the photosphere almost directly "from above," so the
        layer of plasma along the line of sight in which enough matter accumulates for the optical depth to reach one
        (i.e., become opaque) is geometrically relatively thick and extends to a fairly deep level in the photosphere.
        From that depth, radiation is emitted by matter with relatively high temperature, and thus high brightness,
        which reaches the observer.
      </div>
      
      <div style={paragraphStyle}>
        Near the edge of the solar disk, however, the observer looks almost tangentially to the surface layers of the
        solar plasma. In this case, the layer of plasma along the line of sight where enough matter accumulates to reach
        an optical depth of one lies much shallower—thus it is a layer of cooler and less luminous plasma.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/limb_darkening.webp' alt='Limb Darkening' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://science.nasa.gov/learn/heat/big-ideas/big-idea-3-2/'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            science.nasa.gov
          </a>
        </p>
      </div>

      <h3>Differential Rotation of the Sun</h3>
      <div style={paragraphStyle}>
        By observing the movement of sunspots across the solar disk, it is very easy to notice differences in angular
        velocity with which various parts of the convective layer, located at different heliographic latitudes, revolve
        around the Sun’s axis of rotation. This phenomenon is called{' '}
        <strong>the differential rotation of the Sun</strong>.
      </div>
      
      <div style={paragraphStyle}>
        At the level of the photosphere, the plasma near the solar equator completes one full rotation in just 25 days;
        at a heliographic latitude of 45 degrees, this period increases to 27.6 days, and near the poles it reaches
        30.8 days. Interestingly, the results of helioseismological research—that is, studies of the Sun’s interior
        based on the analysis of vibrations of its visible surface (a kind of solar seismology)—indicate that the part
        of the Sun lying beneath the convective layer rotates more like a rigid body. The transition from rigid to
        differential rotation of the plasma occurs at the base of the convective layer, in the so-called{' '}
        <strong>tachocline</strong>.
      </div>
      
      <div style={paragraphStyle}>
        Differential rotation can be easily observed and its variations across different heliographic latitudes
        measured using the projection method of solar observation. For this, it is sufficient to carefully mark the
        positions of all visible sunspots on a drawing of the solar disk every clear day over an extended period, also
        noting the time of each observation. By then carefully measuring both the daily changes in the positions of
        selected spots on the disk and the changes in their relative positions, it is easy to detect that the
        fastest-moving spots are those located closest to the equator, while the slowest are the ones farthest from it.
      </div>
      
      <div style={paragraphStyle}>
        Sunspots move across the solar disk not only with a speed corresponding to the local differential rotation
        velocity but also exhibit some proper motion relative to the surrounding photosphere. Detecting the proper
        motion of sunspots is not particularly difficult either; it merely requires careful marking of the relative
        positions of spots within individual sunspot groups.
      </div>


      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/rotation_of_the_sun.webp' alt='Rotation of the Sun' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://science.nasa.gov/learn/heat/big-ideas/big-idea-3-2/'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            science.nasa.gov
          </a>
        </p>
      </div>

      <h3>Plasma in a Magnetic Field</h3>
      <div style={paragraphStyle}>
        Solar plasma is an excellent conductor of electricity; in fact, it can be approximated as nearly ideal since
        its conductivity is higher than that of copper! In such plasma, a phenomenon called{' '}
        <strong>magnetic field freezing</strong> (or magnetic flux freezing) occurs. The mechanism of this phenomenon is
        very simple: any movement of the magnetic field relative to the plasma (or plasma relative to the field) is
        essentially the movement of a perfect conductor in a magnetic field. This is exactly the operation of a
        well-known electromagnetic generator, and the magnetic field generated in this process cancels out all effects
        of relative motion between the plasma and the original magnetic field.
      </div>

      <div style={paragraphStyle}>
        Thus, if the plasma is relatively dense and the magnetic field induction is relatively weak, every displacement
        of the plasma will generate such a magnetic field that “pulls along” the original magnetic field—meaning the
        magnetic field is “dragged” with the plasma. Conversely, when the magnetic field induction is strong and the
        plasma is sparse, the movements of the magnetic field cause the plasma to move.
      </div>
      
      <div style={paragraphStyle}>
        Typically, in the convective layer (i.e., beneath the photosphere), the energy of plasma motion dominates, so
        the plasma drags the magnetic field with it. In contrast, in the solar atmosphere above the photosphere, it is
        the magnetic field that determines the distribution and motion of the plasma. Typical examples of plasma
        interacting with magnetic fields are sunspots, coronal loops, and solar prominences.
      </div>
      
      <div style={paragraphStyle}>
        The temperature of coronal plasma depends not only on the geometric height above the conventional photosphere
        level but also on local and time-varying processes of interaction between magnetic fields and plasma, especially
        spectacular in active regions and solar flares. Due to the extremely low optical thickness of coronal matter
        and the strong anisotropy of many plasma parameters (including thermal conductivity in strong magnetic fields),
        the solar corona exhibits, sometimes in close proximity, structures that are relatively cool and very hot.
        Observed there are prominences with temperatures around T ~ (6–8) × 10<sup>3</sup> K, coronal loops reaching
        temperatures of T ~ (1–2) × 10<sup>6</sup> K, and flare cores where plasma temperature exceeds T ~ 2 × 10<sup>7</sup>{' '}
        K.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/plasma.webp' alt='Plasma in magnetic field' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://sdo.gsfc.nasa.gov/assets/img/latest/f_211_193_171pfss_2048.jpg'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            sdo.gsfc.nasa.gov
          </a>
        </p>
      </div>

      <h3>Regeneration of Magnetic Fields on the Sun</h3>
      <div style={paragraphStyle}>
        The entire complex of physical processes involved in the regeneration and strengthening of magnetic fields on
        the Sun is called the solar dynamo. The solar dynamo operates in a region about 20,000 km thick, extending near
        the tachocline, at the base of the convective layer, 180,000 km below the photosphere.
      </div>

      <div style={paragraphStyle}>
        As a result of differential rotation, the initial poloidal magnetic field is transformed into a toroidal field
        (i.e., a field whose lines of force are nearly parallel to the solar latitudes) and amplified at the expense of
        the kinetic energy of plasma motions. This field then gradually rises locally toward the Sun’s surface and into
        the solar atmosphere, causing in all plasma layers—from the convective zone to the solar corona—a vast variety
        of phenomena collectively known as solar activity.
      </div>

      <div style={paragraphStyle}>
        At the same time, residual magnetic fields, carried by meridional flow, gradually regenerate the poloidal 
        field but with reversed polarity, and the dynamo cycle can then repeat.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/magnetic_field_sun.webp' alt='Magnetic fields on the Sun' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://wisp.physics.wisc.edu/astro104/lecture14/lec14i.html'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            wisp.physics.wisc.edu
          </a>
        </p>
      </div>

      <h3>Solar Activity Cycle</h3>
      <div style={paragraphStyle}>
        The solar dynamo operates cyclically but is not fully stable. Detailed analyses of changes in solar activity
        indicate that the basic activity cycle has an average length of about 11.2 years; however, significant
        variations in cycle duration occur, reaching even several years. This cycle is only one of many periodicities
        observed in the operation of the solar dynamo (cycles of approximately 88, 203, and possibly 2300 years have
        also been observed).
      </div>
      
      <div style={paragraphStyle}>
        There are also periods when the dynamo’s activity is significantly weakened. The most well-known such period of
        low solar magnetic activity was the Maunder Minimum, which occurred from 1645 to 1715 AD. These solar activity
        minima are associated with periods of climate cooling on Earth.
      </div>
      
      <div style={paragraphStyle}>
        The period of the 11-year cycle is determined based on observations of changes in the number of active
        phenomena on the Sun. However, during this cycle, not only does the induction and flux of magnetic 
        fields reaching the Sun’s surface change—leading to variations in phenomena such as sunspots and 
        flares—but the polarity of the global solar magnetic field also reverses.
      </div>
      
      <div style={paragraphStyle}>
        Thus, a full magnetic activity cycle of the solar dynamo, during which the global magnetic field of the Sun 
        returns to its original polarity configuration, lasts about 22 years.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/solar_activity_circle.webp' alt='Solar Activity Cycle' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://www.swpc.noaa.gov/products/solar-cycle-progression'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            swpc.noaa.gov
          </a>
        </p>
      </div>

      <h3>Sunspots</h3>
      <div style={paragraphStyle}>
        The easiest-to-observe manifestation of the Sun’s magnetic activity are sunspots. Sunspots are dark areas of
        more or less regular shapes, visible against the background of the photosphere. Large sunspots usually consist
        of a dark umbra, which occupies only about 20 percent of the total spot area, surrounded by a somewhat brighter
        penumbra. Only the smallest (often youngest) spots, called pores, lack an umbra. Sunspots generally appear in
        groups, which are the easiest-to-observe elements of active regions—vast complexes of magnetic activity
        extending from the Sun’s interior through the photosphere and chromosphere to the solar corona. The number of
        spots in a group, their distribution, and their stage of development reflect the restructuring of local
        magnetic fields and are the easiest indicators of the evolution of active regions.
      </div>

      <div style={paragraphStyle}>
        The average plasma temperature visible in the umbra of a spot is about 4000–4500 K, which is approximately 1500
        K lower than the temperature of the visible photosphere. The emission of photospheric plasma and plasma in the
        spot is well approximated by the emission of a perfect black body, for which the amount of emitted energy is
        proportional to the fourth power of temperature. Therefore, even a relatively small temperature difference
        between the spot and the photosphere causes a very large brightness difference, making the umbra appear
        practically black against the bright photosphere.
      </div>


      <div style={paragraphStyle}>
        Sunspot diameters range from a few thousand kilometers in the case of pores to over 100,000 km for the largest
        spots, with typical diameters around 10,000–20,000 km. The diameters of large sunspots are so enormous that the
        entire Earth would easily fit within their shadow. Interestingly, spots with diameters exceeding 50,000 km can
        be seen on the solar disk even without optical instruments, using only a high-quality filter that reduces the
        Sun’s brightness to protect the eyes from damage.
      </div>
      

      <div style={paragraphStyle}>
        Sunspots form in areas where a very strong, vertically oriented magnetic field is present at the photosphere
        level, with an induction reaching 0.2–0.4 T (2000–4000 Gauss). By comparison, the Earth’s magnetic field
        induction is on the order of 0.5 Gauss. Such a strong local magnetic field with more or less parallel and
        vertically oriented field lines can be imagined as a gigantic magnetic cylinder (or a magnetically filled tube)
        inserted into the plasma. In such a system, the efficiency of convective energy transport from the Sun’s
        interior is significantly reduced within the spot, causing temperature and plasma opacity to decrease.
        Consequently, not only is the plasma in the umbra darker than the photosphere in the visible layer, but this
        layer is also located several hundred kilometers below the level of the visible photospheric plasma (this
        phenomenon is called the Wilson depression).
      </div>

      <div style={paragraphStyle}>
        Since the sum of the plasma gas pressure and the magnetic pressure inside the tube must equal the gas pressure 
        of the plasma outside the tube (so the entire system remains in equilibrium), the plasma pressure inside 
        the tube is slightly lower than at the same level in the photosphere.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/sunspot.webp' alt='Sunspot' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://hamwaves.com/solar.cycle/en/index.html'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            hamwaves.com
          </a>
        </p>
      </div>

      <h3>Photospheric Faculae</h3>
      <div style={paragraphStyle}>
        The magnetic field concentrated in active regions not only causes the formation of dark sunspots but also bright
        areas called photospheric faculae, which are regions slightly brighter—and about 300 K hotter—than the
        surrounding photosphere. They are particularly easy to notice near the edges of the solar disk, where the limb
        darkening effect significantly increases contrast. Photospheric faculae often appear before the first sunspots
        form in an active region and only disappear after the sunspots vanish. Faculae are visible in areas where the
        magnetic field induction reaches about 0.1 T (around 1000 Gauss). Bright photospheric faculae can also be
        observed using the solar projection method.
      </div>

      <h3>Solar Flares</h3>
      <div style={paragraphStyle}>
        A solar flare is an extremely complex set of phenomena and processes caused by the sudden release of enormous
        amounts of energy in the solar atmosphere (up to about 10<sup>25</sup>–10<sup>27</sup> joules in a single flare)
        ,previously stored in non-potential magnetic fields of active regions. The duration of a flare ranges from
        several minutes for the weakest events to several or even a dozen hours for the strongest or longest-lasting
        ones. The phenomena and processes that together form a solar flare occur in all layers of the solar
        atmosphere—from the photosphere to the upper corona—but can also propagate into interplanetary space and even
        reach the vicinity of Earth.
      </div>
      
      <div style={paragraphStyle}>
        During a flare, huge amounts of energy are emitted as electromagnetic waves (from gamma rays to radio waves),
        streams of particles (electrons, protons, ions) moving at speeds up to 70% of the speed of light, and gigantic
        mass ejections known as coronal mass ejections (CMEs). Typically, a solar flare proceeds in several phases, the
        most spectacular of which is the so-called impulsive phase, during which the sudden release of magnetic energy
        causes a rapid (lasting seconds to minutes) increase in electromagnetic radiation intensity. This is followed
        by a much longer decay phase when the release of magnetic energy ceases, and the heated flare plasma gradually
        cools down. Usually, during strong flares, significant restructuring of local magnetic fields occurs, associated
        with the formation of magnetic loop arcades, prominence eruptions, coronal mass ejections, etc.
      </div>

      <div style={paragraphStyle}>
        The energy source of flares is the coronal magnetic field regions where magnetic fields with anti-parallel
        induction vectors are especially close. There, the phenomenon of magnetic reconnection takes place, during
        which magnetic energy is extraordinarily efficiently converted into other forms of energy—for example, radiation
        ,thermal energy, and kinetic energy of non-thermal plasma particle motions. This energy causes, among other
        effects, a significant local increase in plasma temperature, especially in flare loops, reaching temperatures
        exceeding 20 million K.
      </div>

      <div style={paragraphStyle}>
        A solar flare observed in visible radiation in narrow spectral bands (e.g., in the hydrogen H-alpha line,
        wavelength 656.3 nm) appears as very strong brightening of chromospheric fragments, often developing into
        parallel bands (so-called flare ribbons) at the footpoints of magnetic loop arcades. The strongest flares can be
        visible even in white light as local brightening of the photosphere in active regions (usually within sunspot
        groups).
      </div>

      <div style={paragraphStyle}>
        The course of interaction between magnetic fields and other fields and plasma in each solar flare depends on the
        individual characteristics of the topology and dynamics of local magnetic fields, both at the footpoints and in
        the atmosphere.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/p_rudawy_solar_flares.webp' alt='Solar Flare' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source: P. Rudawy, IA UWr
        </p>
      </div>

      <h3>SOLAR ACTIVITY CYCLE</h3>
      <div style={paragraphStyle}>
        The solar dynamo regenerates and strengthens the Sun’s magnetic fields with cyclically varying efficiency,
        which can be directly observed through the cyclic variation in the number of active phenomena on the Sun,
        lasting on average about 11.2 years (though significant disturbances in cycle length are often observed,
        sometimes lasting several years).
      </div>
      <div style={paragraphStyle}>
        During each cycle, there is first a gradual increase in activity (lasting 3–4 years) until a maximum is reached,
        followed by a gradual decrease in the number of all manifestations of solar activity (usually lasting 6–7
        years). Throughout the cycle, not only does the flux and induction of magnetic fields reaching from the Sun’s
        interior to its surface change, but the polarity of the Sun’s global magnetic field also reverses. Thus, a full
        solar dynamo activity cycle, during which the global magnetic field returns to its original polarity
        configuration, lasts about 22 years.
      </div>
      <div style={paragraphStyle}>
        Detailed analyses of solar activity changes indicate that the 11-year cycle is only one of many periodicities
        occurring in the solar dynamo’s operation (cycles of about 88 and 203 years have also been observed, and
        possibly a 2300-year cycle as well). Irregularly, there are also periods when the dynamo’s activity weakens
        significantly, almost disappearing entirely. Such periods are called grand minima of solar activity. The most
        famous grand minimum, the Maunder Minimum, occurred at the turn of the 16th and 17th centuries (1645–1715) and
        was accompanied by a noticeable cooling of Earth’s climate.
      </div>

      <h3>Activity Belts</h3>
      <div style={paragraphStyle}>
        A plot showing the temporal changes in the positions of sunspots on the solar disk is called a butterfly diagram
        because of its shape resembling butterfly wings. A careful observer of sunspots, using the butterfly diagram,
        can notice another very interesting feature of magnetic field emergence: when sunspots near the end of a certain
        cycle are concentrated close to the solar equator, sunspots associated with the next cycle already appear at
        higher heliographic latitudes, around 40 degrees.
      </div>

      <h3>The Sun’s Atmosphere</h3>
      <div style={paragraphStyle}>
        The Sun is conventionally divided into the part called the solar interior, which consists of the core, the
        radiative zone, and the convective zone, and the part called the solar atmosphere, which includes the
        photosphere, chromosphere, and corona.
      </div>

      <div style={paragraphStyle}>
        Because sunlight scattered in the Earth’s atmosphere is brighter than the brightness of the Sun’s atmosphere,
        the latter is practically invisible during standard observations from the Earth’s surface. Only during a total
        solar eclipse, when the Moon covers the solar disk, can the chromosphere and solar corona be seen, sometimes
        even prominences. The corona has a very low density but is very extensive—it covers a region with a diameter of
        several tens of solar radii and gradually transitions into the solar wind.
      </div>

      <div style={paragraphStyle}>
        Loop structures visible in the solar corona indicate that its spatial structure is shaped by the solar magnetic
        field. Its influence on the coronal plasma, solar wind, and Earth’s magnetosphere is enormous. Magnetic fields
        cause all manifestations of solar activity, starting from sunspots on the disk to flares and coronal mass
        ejections, magnetic storms on Earth, and changes in the heliosphere’s properties. The magnetic field also
        affects the matter flowing out from the Sun in the form of the solar wind.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/sun_atmosphere.webp' alt='Sun Atmosphere' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source:&nbsp;
          <a
            href='https://www.mps.mpg.de/solar-physics/ssc'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#555', textDecoration: 'underline' }}
          >
            mps.mpg.de
          </a>
        </p>
      </div>

      <h3>Temperature of the Solar Corona</h3>
      <div style={paragraphStyle}>
        The layers of solar plasma lying above the photosphere, called the solar atmosphere, emit radiation at various
        wavelengths—from X-rays to radio waves. The typical plasma temperature in quiet regions of the solar corona
        ranges from 1 to 2 million kelvin (MK), but locally, in post-flare loops, it can reach 10 MK or more.
      </div>
      <div style={paragraphStyle}>
        A very efficient mechanism for global coronal heating may be so-called solar "nano-flares." Each nano-flare
        releases a relatively small amount of energy, on the order of approximately 10<sup>16</sup> J (below the
        detection threshold), but many such nano-flares can occur simultaneously at any given moment. Energy can also
        be transported to the corona by magneto-acoustic waves (waves propagating in magnetized plasma).
      </div>

      <h3>Solar Prominences</h3>
      <div style={paragraphStyle}>
        Solar prominences are large and massive structures of relatively dense and cool plasma, supported against the
        gravitational field and structured by magnetic fields, visible above the solar limb. The magnetic skeletons of
        solar prominences are anchored in the Sun's interior and extend through the photosphere and chromosphere into
        the corona. As a result, the visible forms and evolution of prominences are governed by magnetic fields. The
        plasma observed as a prominence is transferred from the Sun’s interior into the prominence’s magnetic structure
        (a magnetic hammock) in the solar corona.
      </div>

      <div style={paragraphStyle}>
        Prominences emit primarily by scattering radiation arriving from the photosphere and chromosphere, while their
        own emission is relatively weak. For this reason, prominences appear bright against the very faint solar corona
        and dark sky, but they appear dark when observed against the solar disk, where they are called filaments.
        Well-developed quiescent prominences are curtain-like structures, with a typical length:height:width ratio of
        100:10:1. This vertical curtain, called the spine, is connected to the chromosphere by legs. Both spines and
        legs are composed of thin threads of plasma that trace the magnetic field lines.
      </div>

      <div style={paragraphStyle}>
        Prominences can be subdivided into three main types: <strong>quiescent</strong>, <strong>activated</strong>, and
        <strong>eruptive</strong>. The mass of a prominence can reach up to 10<sup>11</sup> kg, and the length of a
        quiescent prominence can extend up to 1 million kilometers. Their lifetimes can last from days to even several
        months. Eruptive prominences reflect the rapid evolution of the magnetic system, releasing the visible
        prominence plasma. The velocities of erupting prominences can reach up to 2000 km/s (for comparison, the escape
        velocity from the Sun is 617 km/s).
      </div>

      <div style={paragraphStyle}>
        Prominences can be observed from the ground with any ordinary telescope during a total solar eclipse as bright,
        pink structures, because their emission is dominated by a mix of two hydrogen spectral lines: H-alpha (red) and
        H-beta (green). Outside of eclipses, prominences can be observed from the ground using telescopes (particularly
        coronagraphs) equipped with narrow-band filters. Currently, a large number of prominence observations are
        carried out by space probes.
      </div>

      <div style={{ textAlign: 'center' }}>
        <ImgTemplate srcName='heliophysics/p_rudawy_solar_prominences.webp' alt='Solar Prominences' width='100%' />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Source: P. Rudawy, IA UWr
        </p>
      </div>
    </div>
  )
}

Heliophysics.propTypes = { children: node }

export default Heliophysics
